export const normalizeRestException = (error) => {
  const candidate =
    error?.cause && typeof error.cause === 'object' ? error.cause : error;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  if (!candidate.type && !candidate.message && !candidate.details) {
    return null;
  }

  return {
    type: candidate.type,
    message: candidate.message || error?.message,
    details: candidate.details,
    timestamp: candidate.timestamp,
    httpStatusCode: candidate.httpStatusCode,
  };
};

let notifier = null;
const pendingNotifications = [];

export const setNotifier = (nextNotifier) => {
  notifier = typeof nextNotifier === 'function' ? nextNotifier : null;
  if (notifier && pendingNotifications.length) {
    const queued = pendingNotifications.splice(0, pendingNotifications.length);
    queued.forEach((payload) => {
      try {
        notifier(payload);
      } catch (notifyError) {
        console.warn('Failed to dispatch queued notification:', notifyError);
      }
    });
  }
};

const enqueueNotification = (payload) => {
  if (!payload) {
    return;
  }
  if (!notifier) {
    pendingNotifications.push(payload);
    return;
  }
  try {
    notifier(payload);
  } catch (notifyError) {
    console.warn('Failed to dispatch notification:', notifyError);
  }
};

export const notifyGlobal = ({ level = 'info', message, details, duration } = {}) => {
  enqueueNotification({
    level,
    message,
    details,
    duration
  });
};

const notifyMeta = (meta) => {
  if (!meta) {
    return;
  }
  enqueueNotification({
    level: meta.type?.toLowerCase(),
    message: meta.message,
    details: meta.details
  });
};

const shouldNotifyMeta = (meta) => {
  const type = String(meta?.type || '').toUpperCase();
  return type === 'WARNING' || type === 'INFO' || type === 'ERROR';
};

const getMetaType = (meta) => String(meta?.type || '').toUpperCase();

const isRestExceptionPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const type = getMetaType(payload);
  if (!type || !payload.message) {
    return false;
  }
  if (type !== 'WARNING' && type !== 'INFO' && type !== 'ERROR') {
    return false;
  }
  const hasData = Object.prototype.hasOwnProperty.call(payload, 'data');
  const hasMeta = Object.prototype.hasOwnProperty.call(payload, 'meta');
  return !hasData && !hasMeta;
};

const normalizeServiceResponse = (result) => {
  if (!result || typeof result !== 'object') {
    return { data: result, meta: null };
  }

  const hasData = Object.prototype.hasOwnProperty.call(result, 'data');
  const hasMeta = Object.prototype.hasOwnProperty.call(result, 'meta');

  if (hasData && hasMeta) {
    return { data: result.data, meta: result.meta };
  }

  if (isRestExceptionPayload(result)) {
    return { data: null, meta: result };
  }

  return { data: result, meta: null };
};

/**
 * @param {Error} error
 * @param {{ skipNotify?: boolean }} [options]
 */
export const handleServiceError = (error, options = {}) => {
  const meta = normalizeRestException(error) || {
    type: 'ERROR',
    message: error?.message || 'Request failed',
    details: error?.cause,
    httpStatusCode: error?.httpStatusCode,
  };
  if (meta) {
    if (!options.skipNotify && shouldNotifyMeta(meta)) {
      notifyMeta(meta);
    }
    return { data: null, meta };
  }
  return { data: null, meta: null };
};

/**
 * @param {() => Promise<any>} call
 * @param {{ skipNotify?: boolean }} [options] - skipNotify: nie wywołuj notifyMeta przy błędzie (gdy caller sam obsłuży)
 */
export const wrapServiceCall = async (call, options = {}) => {
  try {
    const result = await call();
    const { data, meta } = normalizeServiceResponse(result);

    if (!options.skipNotify && meta && shouldNotifyMeta(meta)) {
      notifyMeta(meta);
    }

    return { data, meta };
  } catch (error) {
    return handleServiceError(error, options);
  }
};
